import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, filter } from 'rxjs/operators';
import { UsersRepository } from '../../infrastructure/http/repositories/users.repository';
import { RolesRepository } from '../../infrastructure/http/repositories/roles.repository';
import { LoggingService } from '../../core/services/logging.service';
import { UsersPageActions, UsersAPIActions } from './users.actions';
import {
  selectCurrentFilter,
  selectRoles,
  selectSortBy,
  selectSortOrder,
} from './users.selectors';

@Injectable()
export class UsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly usersRepository = inject(UsersRepository);
  private readonly rolesRepository = inject(RolesRepository);
  private readonly logger = inject(LoggingService);

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UsersPageActions.loadUsers,
        UsersPageActions.search,
        UsersPageActions.sort,
        UsersPageActions.changePage,
        UsersPageActions.changePageSize,
        UsersPageActions.filterByStatus,
        UsersPageActions.filterByRole,
        UsersPageActions.clearFilters,
        UsersPageActions.refresh
      ),
      concatLatestFrom(() => [
        this.store.select(selectCurrentFilter),
        this.store.select(selectSortBy),
        this.store.select(selectSortOrder),
      ]),
      switchMap(([, currentFilter, sortBy, sortOrder]) => {
        const filter = { ...currentFilter, sortBy, sortOrder };
        this.logger.debug('Loading FPS users', { filter });
        return this.usersRepository.getUsers(filter).pipe(
          map(result => {
            this.logger.debug('FPS users loaded', {
              totalCount: result.totalCount,
              pageNumber: result.pageNumber,
            });
            return UsersAPIActions.loadUsersSuccess({ result });
          }),
          catchError(error => {
            this.logger.error('Failed to load FPS users', error);
            return of(
              UsersAPIActions.loadUsersFailure({
                error: 'Failed to load users. Please try again.',
              })
            );
          })
        );
      })
    )
  );

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersPageActions.loadRoles),
      concatLatestFrom(() => this.store.select(selectRoles)),
      filter(([, existingRoles]) => existingRoles.length === 0),
      switchMap(() => {
        this.logger.debug('Loading roles');
        return this.rolesRepository.getRoles().pipe(
          map(roles => {
            this.logger.debug('Roles loaded', { count: roles.length });
            return UsersAPIActions.loadRolesSuccess({ roles });
          }),
          catchError(error => {
            this.logger.error('Failed to load roles', error);
            return of(
              UsersAPIActions.loadRolesFailure({
                error: 'Failed to load roles.',
              })
            );
          })
        );
      })
    )
  );
}
